import React, { useCallback, useEffect, useId, useState } from "react";
import { LockKeyhole } from "lucide-react";
import { Button } from "../../../components/new-components/ui/button";
import { Textarea } from "../../../components/new-components/ui/textarea";
import {
  PrivateNote,
  PrivateNotesPage,
  privateNotesApi,
} from "./privateNotesApi";

/**
 * Private provider notes (Isaiah Cotto, 2026-10-21; asked by provider Cindy Aragon): what the providers and the
 * office need to know about a resident ("patient is aggressive, cognitively impaired") that the facility must never
 * see. The manifest notes above this, in the workbench, are what the facility receives; these are not.
 *
 * Mounted by the resident workbench at its `private-notes-slot`. The server decides who reads (the providers who
 * work the resident and the office) and who writes; `canWrite` only decides whether the add box is offered, and the
 * server's own `can_write` must agree. Notes are append-only: no edit, no delete, no autosave. Every body is
 * rendered as text, never as markup.
 */
export interface PrivateNotesProps {
  facilityId: string;
  patientId: string;
  canWrite: boolean;
}

export const PRIVATE_NOTE_LABEL =
  "Private note: only providers and the office see this, never the facility";

const FALLBACK_MAX = 2000;

/*
 * The server's rules, said before sending (services/emerge/providerPrivateNotes.js): a note made only of characters
 * that render as nothing (whitespace, NBSP, zero-width, LRM/RLM, the Arabic letter mark, BOM, ...) says nothing, and
 * a lone UTF-16 surrogate is not a character. Built from code points so the source holds no invisible character.
 */
const hex4 = (c: number) => c.toString(16).padStart(4, "0");
const INVISIBLE_RANGES: number[][] = [[0x00a0], [0x00ad], [0x034f], [0x061c], [0x115f, 0x1160], [0x17b4, 0x17b5], [0x180e],
  [0x2000, 0x200f], [0x2028, 0x202f], [0x205f, 0x206f], [0x3000], [0x3164], [0xfe00, 0xfe0f], [0xfeff], [0xffa0]];
const INVISIBLE = new RegExp(
  `[\\s${INVISIBLE_RANGES.map(([a, b]) => (b ? `\\u${hex4(a)}-\\u${hex4(b)}` : `\\u${hex4(a)}`)).join("")}]`,
  "gu",
);
// Constructed, not a literal: the project compiles to ES5, where TypeScript refuses a `u`-flag literal.
const LONE_SURROGATE = new RegExp("\\p{Cs}", "u");
export function noteIsWritable(text: string): boolean {
  return text.replace(INVISIBLE, "").length > 0 && !LONE_SURROGATE.test(text);
}

function newKey(): string {
  const c = typeof window !== "undefined" ? window.crypto : undefined;
  if (c && typeof c.randomUUID === "function") return `pn-${c.randomUUID()}`;
  return `pn-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

function when(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "time not recorded";
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

type Load =
  | { state: "loading" }
  | { state: "ready"; page: PrivateNotesPage }
  | { state: "refused"; message: string }
  | { state: "error"; message: string };

function refusalWords(status: number | undefined, code: string | undefined, message: string | undefined): Load {
  if (status === undefined && !code) {
    return { state: "error", message: "Could not reach eMerge to load the private notes." };
  }
  if (code === "PRIVATE_NOTES_NOT_INSTALLED") {
    return { state: "refused", message: "Private notes are not switched on yet." };
  }
  if (status === 403) {
    return {
      state: "refused",
      message: "Private notes are for the providers who work this resident and the office.",
    };
  }
  if (status === 404) {
    return { state: "refused", message: message || "This resident is not confirmed at this building." };
  }
  return { state: "error", message: message || "The private notes could not be loaded." };
}

function NoteItem({ note, facilityId }: { note: PrivateNote; facilityId: string }) {
  const who = note.author.is_you ? "You" : note.author.name || "A provider";
  return (
    <li className="fw-private-note">
      <p className="fw-muted">
        <strong>{who}</strong> · <time dateTime={note.created_at}>{when(note.created_at)}</time>
        {note.facility.id !== facilityId && note.facility.name ? ` · at ${note.facility.name}` : ""}
        {note.from_merged_record ? " · from a merged resident record" : ""}
      </p>
      {/* Text, never markup: React escapes it, and pre-wrap keeps the writer's line breaks. */}
      <p className="fw-private-note-body" style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
        {note.body}
      </p>
    </li>
  );
}

export function PrivateNotes({ facilityId, patientId, canWrite }: PrivateNotesProps) {
  const ids = useId();
  const headingId = `${ids}-heading`;
  const inputId = `${ids}-input`;
  const helpId = `${ids}-help`;
  const [load, setLoad] = useState<Load>({ state: "loading" });
  const [text, setText] = useState("");
  const [key, setKey] = useState(newKey);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saved, setSaved] = useState(false);

  const fetchNotes = useCallback(async () => {
    setLoad({ state: "loading" });
    try {
      const r = await privateNotesApi.list(facilityId, patientId);
      // The shape is checked, not assumed: a malformed answer is an error, never a crash in the drawer.
      if (r.success && r.data && Array.isArray(r.data.notes)) setLoad({ state: "ready", page: r.data });
      else if (r.success) setLoad({ state: "error", message: "The private notes could not be loaded." });
      else setLoad(refusalWords(r.status, r.code, r.message));
    } catch {
      setLoad({ state: "error", message: "Could not reach eMerge to load the private notes." });
    }
  }, [facilityId, patientId]);

  useEffect(() => {
    setText("");
    setSaveError("");
    setSaved(false);
    setKey(newKey());
    fetchNotes();
  }, [fetchNotes]);

  const page = load.state === "ready" ? load.page : null;
  const max = page?.max_length || FALLBACK_MAX;
  const writable = canWrite && page !== null && page.can_write;
  const length = Array.from(text).length;
  const ready = noteIsWritable(text) && length <= max && !saving;

  // Not a <form>: the panel sits inside the workbench's manifest form, and forms do not nest. Save is a plain
  // button; Enter in the box is a new line, as in every notes field here.
  const save = async () => {
    if (!ready || !page) return;
    setSaving(true);
    setSaveError("");
    setSaved(false);
    try {
      const r = await privateNotesApi.add(facilityId, patientId, text, key);
      if (r.success && r.data) {
        const added = r.data.note;
        if (added) {
          setLoad({
            state: "ready",
            page: { ...page, notes: [added, ...page.notes.filter((n) => n.id !== added.id)] },
          });
        } else {
          await fetchNotes();
        }
        setText("");
        setKey(newKey());
        setSaved(true);
      } else if (r.status === undefined && !r.code) {
        // Never reached, or the answer was lost: keep the text and the key, so trying again cannot save it twice.
        setSaveError("eMerge did not confirm the note. Nothing is shown as saved. Try again; it will not be saved twice.");
      } else {
        setSaveError(r.message || r.error || "The note was not saved.");
        setKey(newKey());
      }
    } catch {
      setSaveError("eMerge did not confirm the note. Nothing is shown as saved. Try again; it will not be saved twice.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="fw-private-notes" aria-labelledby={headingId}>
      <h3 id={headingId} className="fw-replacement-title">
        <LockKeyhole size={14} aria-hidden="true" /> Private notes
      </h3>
      <p className="fw-muted">Only providers and the office see these notes. The facility never does.</p>

      {load.state === "loading" && (
        <p role="status" className="fw-muted">Loading private notes…</p>
      )}
      {load.state === "refused" && <p className="fw-muted">{load.message}</p>}
      {load.state === "error" && (
        <div role="status" className="fw-error">
          <p>{load.message}</p>
          <Button variant="outline" type="button" onClick={fetchNotes}>
            Try again
          </Button>
        </div>
      )}

      {page && (
        <>
          {page.access === "AUTHOR_ONLY" && (
            <p className="fw-muted">
              You see only the notes you wrote: you no longer work this resident.
            </p>
          )}
          {page.notes.length === 0 ? (
            <p className="fw-muted">No private notes yet.</p>
          ) : (
            <ul className="fw-private-note-list" aria-label="Private notes, newest first">
              {page.notes.map((n) => (
                <NoteItem key={n.id} note={n} facilityId={facilityId} />
              ))}
            </ul>
          )}
          {page.truncated && (
            <p className="fw-muted">Showing the newest {page.limit} notes.</p>
          )}

          {writable && (
            <div role="group" aria-label="Add a private note">
              <label htmlFor={inputId}>{PRIVATE_NOTE_LABEL}</label>
              <Textarea
                id={inputId}
                rows={4}
                value={text}
                readOnly={saving}
                onChange={(e) => {
                  setText(e.target.value);
                  setSaved(false);
                }}
                aria-describedby={helpId}
                aria-invalid={length > max}
              />
              <p id={helpId} className="fw-muted">
                {length} / {max} characters. Saved notes cannot be edited or deleted; add a new note to correct one.
              </p>
              {saveError && (
                <p role="alert" className="fw-error">
                  {saveError}
                </p>
              )}
              <p role="status" className="fw-save-status">
                {saving ? "Saving the private note…" : saved ? "Private note saved." : ""}
              </p>
              <div className="fw-actions">
                <Button className="fw-primary" type="button" onClick={save} disabled={!ready}>
                  {saving ? "Saving…" : "Save private note"}
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}
export default PrivateNotes;
